import React, { useEffect, useState } from 'react';
import { Button, Modal, Statistic, Table, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ApolloError } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import {
  type DspBillBatchPaymentPreview,
  type DspBillBatchPaymentRowError,
  type DspBillCurrencyAmount,
  useConfirmDspBillBatchPayment,
  usePreviewDspBillBatchPayment,
} from '../../../../graphql/mutations/settlement/dspBillPayment';
import { formatCentsAmount } from '../../../../utils/invoiceAmount';
import { downloadDspBillBatchPaymentTemplate } from '../../../../utils/dspBillBatchPaymentCsv';

const MAX_FILE_SIZE = 1024 * 1024;
const DSP_BILL_BATCH_PAYMENT_STALE_KEY = 'errors.business.settlement.dspBillBatchPayment.stale';

const readFileAsBase64 = (file: File, failText: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(failText));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(failText));
        return;
      }
      const idx = result.indexOf('base64,');
      resolve(idx >= 0 ? result.slice(idx + 'base64,'.length) : result);
    };
    reader.readAsDataURL(file);
  });

// errorLink 已弹 toast 的情形：BUSINESS_ERROR 的两个特定 messageKey、以及 INTERNAL_ERROR / 未知 code
const ERROR_LINK_TOASTED_MESSAGE_KEYS = ['errors.system.serviceUnavailable', 'errors.business.order.listingQueryTooBroad'];

// errorLink 对 VALIDATION_ERROR / 多数 BUSINESS_ERROR 只 console 不弹提示，需页面自行反馈后端文案
const toastBatchPaymentError = (e: unknown) => {
  // 网络错误 errorLink 已弹提示
  if ((e as ApolloError | null)?.networkError) return;
  const first = (e as { graphQLErrors?: ReadonlyArray<GraphQLFormattedError> } | null)?.graphQLErrors?.[0];
  if (first) {
    const code = first.extensions?.code;
    const messageKey = first.extensions?.messageKey;
    if (code !== 'VALIDATION_ERROR' && code !== 'BUSINESS_ERROR') return;
    if (typeof messageKey === 'string' && ERROR_LINK_TOASTED_MESSAGE_KEYS.includes(messageKey)) return;
  }
  const extMessage = first?.extensions?.message;
  const text =
    (typeof extMessage === 'string' && extMessage) ||
    first?.message ||
    (e instanceof Error ? e.message : '');
  if (text) message.error(text);
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DspBillBatchPaymentModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [previewMutation, { loading: previewing }] = usePreviewDspBillBatchPayment();
  const [confirmMutation, { loading: confirming }] = useConfirmDspBillBatchPayment();
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null);
  const [preview, setPreview] = useState<DspBillBatchPaymentPreview | null>(null);
  const [errors, setErrors] = useState<DspBillBatchPaymentRowError[]>([]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setErrors([]);
    }
  }, [open]);

  const handleBeforeUpload = (f: File) => {
    if (!f.name.toLowerCase().endsWith('.csv')) {
      message.error(t('dspBillBatchPayment.invalidExt'));
      return Upload.LIST_IGNORE;
    }
    if (f.size === 0) {
      message.error(t('dspBillBatchPayment.emptyFile'));
      return Upload.LIST_IGNORE;
    }
    if (f.size > MAX_FILE_SIZE) {
      message.error(t('dspBillBatchPayment.tooLarge'));
      return Upload.LIST_IGNORE;
    }
    setFile(null);
    setPreview(null);
    setErrors([]);
    void (async () => {
      try {
        const base64 = await readFileAsBase64(f, t('dspBillBatchPayment.readFailed'));
        const res = await previewMutation({ variables: { fileBase64: base64, fileName: f.name } });
        const data = res.data?.previewDspBillBatchPayment;
        if (!data) return;
        setFile({ name: f.name, base64 });
        if (data.errors.length > 0) {
          setErrors(data.errors);
          setPreview(null);
        } else {
          setErrors([]);
          setPreview(data);
        }
      } catch (e) {
        console.error(e);
        toastBatchPaymentError(e);
      }
    })();
    return false;
  };

  const handleConfirm = async () => {
    if (!file || !preview || !preview.fingerprint) return;
    try {
      const res = await confirmMutation({
        variables: {
          fileBase64: file.base64,
          fileName: file.name,
          expectedBillCount: preview.billCount,
          expectedFingerprint: preview.fingerprint,
        },
      });
      const data = res.data?.confirmDspBillBatchPayment;
      if (!data) return;
      if (data.errors.length > 0) {
        setErrors(data.errors);
        setPreview(null);
        return;
      }
      message.success(t('dspBillBatchPayment.successMsg', { count: data.paidCount }));
      onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
      toastBatchPaymentError(e);
      // 预览已过期：清空文件与预览，强制重新上传；BUSY 保留预览供重试
      const staleKey = (e as { graphQLErrors?: ReadonlyArray<GraphQLFormattedError> } | null)?.graphQLErrors?.[0]
        ?.extensions?.messageKey;
      if (staleKey === DSP_BILL_BATCH_PAYMENT_STALE_KEY) {
        setFile(null);
        setPreview(null);
      }
    }
  };

  const renderTotals = (totals: DspBillCurrencyAmount[], color: string) =>
    totals.length === 0 ? (
      '-'
    ) : (
      <div>
        {totals.map((x) => (
          <div key={x.currency} className="font-bold" style={{ color }}>{formatCentsAmount(x.amount, x.currency)}</div>
        ))}
      </div>
    );

  const columns: ColumnsType<DspBillBatchPaymentRowError> = [
    { title: t('dspBillBatchPayment.colRowNo'), dataIndex: 'rowNo', width: 80 },
    { title: t('dspBillBatchPayment.colBillId'), dataIndex: 'billId', width: 180, render: (v?: string | null) => v || '-' },
    { title: t('dspBillBatchPayment.colError'), dataIndex: 'message' },
  ];

  const hasErrors = errors.length > 0;

  return (
    <Modal
      open={open}
      title={t('dspBillBatchPayment.title')}
      onCancel={onClose}
      maskClosable={false}
      width={640}
      footer={
        hasErrors ? (
          <Button onClick={onClose}>{t('dspBillBatchPayment.close')}</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={confirming}>
              {t('dspBillBatchPayment.cancel')}
            </Button>
            <Button type="primary" disabled={!preview} loading={confirming} onClick={handleConfirm}>
              {t('dspBillBatchPayment.confirm')}
            </Button>
          </>
        )
      }
    >
      {hasErrors ? (
        <>
          <div className="mb-3 text-red-500">{t('dspBillBatchPayment.errorTitle')}</div>
          <Table
            rowKey={(r) => `${r.rowNo}-${r.billId ?? ''}-${r.message}`}
            size="small"
            columns={columns}
            dataSource={errors}
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        </>
      ) : (
        <>
          <div className="mb-2">
            <Button type="link" className="px-0" icon={<DownloadOutlined />} onClick={downloadDspBillBatchPaymentTemplate}>
              {t('dspBillBatchPayment.template')}
            </Button>
          </div>
          <Upload accept=".csv" showUploadList={false} maxCount={1} beforeUpload={handleBeforeUpload}>
            <Button icon={<UploadOutlined />} loading={previewing} disabled={confirming}>
              {t('dspBillBatchPayment.selectFile')}
            </Button>
          </Upload>
          <div className="mt-2 text-gray-500">{t('dspBillBatchPayment.hint')}</div>
          {file && <div className="mt-2">{file.name}</div>}
          {preview && (
            <div className="mt-4 flex gap-8">
              <Statistic
                title={t('dspBillBatchPayment.billCount')}
                value={preview.billCount}
                valueStyle={{ fontWeight: 'bold' }}
              />
              <Statistic
                title={t('dspBillBatchPayment.invoiceTotal')}
                valueRender={() => renderTotals(preview.invoiceTotals, '#1677ff')}
              />
              <Statistic
                title={t('dspBillBatchPayment.billTotal')}
                valueRender={() => renderTotals(preview.billTotals, '#52c41a')}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default DspBillBatchPaymentModal;
